import { DateTime } from "luxon";

import LINKS from "@app/common/links";
import type { User } from "@app/common/types";
import { isWorkspaceOwner } from "@app/common/workspaces/workspaceUtils";
import Config from "@app/config";

import * as UserUtils from "./userUtils";

const NEW_POLICY_RELEASE_DATE = DateTime.fromFormat(
  Config.releaseDates.privacyPolicy,
  "MM/dd/yyyy"
);

export const NEW_POLICY_EFFECTIVE_DATE = NEW_POLICY_RELEASE_DATE.plus({
  days: 30,
});

export const newPoliciesReleased = () => {
  return DateTime.now() > NEW_POLICY_RELEASE_DATE;
};

export const newPoliciesAreInEffect = () =>
  DateTime.now() > NEW_POLICY_EFFECTIVE_DATE;

const userCreatedBeforeNewPoliciesReleased = (user: User | null) => {
  return UserUtils.createdBefore(user, NEW_POLICY_RELEASE_DATE);
};

// user should be notified of the new policies if their account was created
// before the new policies were released and the new policies are not already
// in effect
export const userShouldBeNotifiedOfNewPolicies = (user: User | null) =>
  newPoliciesReleased() &&
  !newPoliciesAreInEffect() &&
  userCreatedBeforeNewPoliciesReleased(user);

export const requireAcceptDataProcessingAgreement = (user: User | null) => {
  if (newPoliciesReleased() && user && isWorkspaceOwner(user.workspace)) {
    return (
      user.userMetadata?.lastAcceptedDataProcessingAgreementVersion !==
        Config.policyVersions.dataProcessingAgreement ||
      user.userMetadata?.requireDataProcessingAgreementAcceptReason != null
    );
  }

  return false;
};

export const newUserPrivacyPolicyLink = () =>
  newPoliciesReleased()
    ? LINKS.MARKETING.UPDATED_PRIVACY_POLICY
    : LINKS.MARKETING.PRIVACY_POLICY;

export const newUserTermsOfServiceLink = () =>
  newPoliciesReleased()
    ? LINKS.MARKETING.UPDATED_TERMS_OF_SERVICE
    : LINKS.MARKETING.TERMS_OF_SERVICE;

export const privacyPolicyForUser = (user: User | null) =>
  userCreatedBeforeNewPoliciesReleased(user) && !newPoliciesAreInEffect()
    ? LINKS.MARKETING.PRIVACY_POLICY
    : newUserPrivacyPolicyLink();

export const termsOfServiceForUser = (user: User | null) =>
  userCreatedBeforeNewPoliciesReleased(user) && !newPoliciesAreInEffect()
    ? LINKS.MARKETING.TERMS_OF_SERVICE
    : newUserTermsOfServiceLink();
